import { products, printAreas } from '../data/products.js';

const FRONT_AREA_BASE_KEYS = new Set(['frontA4', 'frontA3']);
const BACK_AREA_BASE_KEYS = new Set(['backA4', 'backA3']);
const CHEST_AREA_BASE_KEYS = new Set(['leftChest', 'rightChest']);

export function getBaseAreaKey(areaKey = '') {
  return String(areaKey || '').replace(/_long$/, '');
}

export function normalizeSelectedPrintAreas(selectedPrintAreas) {
  if (!Array.isArray(selectedPrintAreas)) return [];
  return selectedPrintAreas
    .map((sel) => {
      if (!sel) return null;
      if (typeof sel === 'string') {
        return { areaKey: sel, method: 'print' };
      }
      if (typeof sel !== 'object') return null;
      const areaKey = typeof sel.areaKey === 'string' ? sel.areaKey : '';
      if (!areaKey) return null;
      const method = sel.method === 'embo' ? 'embo' : 'print';
      return { ...sel, areaKey, method };
    })
    .filter(Boolean);
}

export function getExclusiveGroupKey(areaKey = '') {
  const baseAreaKey = getBaseAreaKey(areaKey);
  if (FRONT_AREA_BASE_KEYS.has(baseAreaKey)) return 'front';
  if (BACK_AREA_BASE_KEYS.has(baseAreaKey)) return 'back';
  return '';
}

export function arePrintAreasConflicting(areaKeyA = '', areaKeyB = '') {
  const baseA = getBaseAreaKey(areaKeyA);
  const baseB = getBaseAreaKey(areaKeyB);

  if (!baseA || !baseB) return false;
  if (baseA === baseB) return false;

  const groupA = getExclusiveGroupKey(baseA);
  const groupB = getExclusiveGroupKey(baseB);
  if (groupA && groupA === groupB) return true;

  const isFrontA = FRONT_AREA_BASE_KEYS.has(baseA);
  const isFrontB = FRONT_AREA_BASE_KEYS.has(baseB);
  const isChestA = CHEST_AREA_BASE_KEYS.has(baseA);
  const isChestB = CHEST_AREA_BASE_KEYS.has(baseB);

  return (isFrontA && isChestB) || (isChestA && isFrontB);
}

export function validatePrintAreaSelections({ sku, selectedPrintAreas }) {
  const normalizedAreas = normalizeSelectedPrintAreas(selectedPrintAreas);
  const errors = [];
  const product = products.find((entry) => entry.sku === sku);

  if (!product) {
    return {
      valid: false,
      errors: [{
        code: 'unknown_sku',
        message: 'Unknown product SKU.',
        messageHe: 'מזהה מוצר לא מוכר.',
      }],
      normalizedAreas,
    };
  }

  const allowedAreaKeys = new Set(Array.isArray(product.activePrintAreas) ? product.activePrintAreas : []);
  const seenAreaKeys = new Set();

  for (const selection of normalizedAreas) {
    const areaKey = selection.areaKey;
    if (!printAreas[areaKey]) {
      errors.push({
        code: 'unknown_print_area',
        areaKeys: [areaKey],
        message: `Unknown print area: ${areaKey}.`,
        messageHe: `אזור מיתוג לא מוכר: ${areaKey}.`,
      });
      continue;
    }

    if (!allowedAreaKeys.has(areaKey)) {
      errors.push({
        code: 'print_area_not_allowed_for_product',
        areaKeys: [areaKey],
        message: `Print area ${areaKey} is not allowed for ${sku}.`,
        messageHe: `אזור המיתוג ${areaKey} אינו מותר עבור ${sku}.`,
      });
    }

    if (seenAreaKeys.has(areaKey)) {
      errors.push({
        code: 'duplicate_print_area',
        areaKeys: [areaKey],
        message: `Print area ${areaKey} was selected more than once.`,
        messageHe: `אזור המיתוג ${areaKey} נבחר יותר מפעם אחת.`,
      });
    } else {
      seenAreaKeys.add(areaKey);
    }
  }

  for (let index = 0; index < normalizedAreas.length; index += 1) {
    for (let compareIndex = index + 1; compareIndex < normalizedAreas.length; compareIndex += 1) {
      const firstAreaKey = normalizedAreas[index]?.areaKey || '';
      const secondAreaKey = normalizedAreas[compareIndex]?.areaKey || '';
      if (!firstAreaKey || !secondAreaKey) continue;
      if (!arePrintAreasConflicting(firstAreaKey, secondAreaKey)) continue;

      errors.push({
        code: 'conflicting_print_areas',
        areaKeys: [firstAreaKey, secondAreaKey],
        message: `Print areas ${firstAreaKey} and ${secondAreaKey} cannot be combined.`,
        messageHe: `אי אפשר לשלב בין אזורי המיתוג ${firstAreaKey} ו-${secondAreaKey}.`,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    normalizedAreas,
  };
}
